import { Children, type HTMLAttributes, type ReactNode } from 'react';

type HeadingProps = HTMLAttributes<HTMLHeadingElement> & {
  level?: 1 | 2 | 3;
  /**
   * Editorial pivot (Instrument Serif italic, deep lavender): `true` sets the last word of a plain-text title in the
   * serif; a string/node is appended as the pivot suffix. One pivot per headline, display sizes only (levels 1-2).
   */
  pivot?: boolean | ReactNode;
};

/** Split "Welcome back" -> ["Welcome ", "back"]; titles without a space pivot as a whole word. */
export function splitPivot(title: string): [string, string] {
  const trimmed = title.trimEnd();
  const i = trimmed.lastIndexOf(' ');
  return i < 0 ? ['', trimmed] : [trimmed.slice(0, i + 1), trimmed.slice(i + 1)];
}

export function Heading({ level = 1, pivot, children, ...rest }: HeadingProps) {
  const Tag = (`h${level}`) as 'h1' | 'h2' | 'h3';
  let body: ReactNode = children;
  if (pivot && level < 3) {
    const only = Children.toArray(children);
    if (pivot === true) {
      if (only.length === 1 && typeof only[0] === 'string') {
        const [head, tail] = splitPivot(only[0]);
        body = <>{head}<span className="qm-pivot">{tail}</span></>;
      }
    } else {
      body = <>{children} <span className="qm-pivot">{pivot}</span></>;
    }
  }
  return <Tag {...rest} data-level={level} className={['qm-heading', rest.className].filter(Boolean).join(' ')}>{body}</Tag>;
}

export function Text({ tone = 'default', ...rest }: HTMLAttributes<HTMLParagraphElement> & { tone?: 'default' | 'muted' }) {
  return <p {...rest} data-tone={tone} className={['qm-text', rest.className].filter(Boolean).join(' ')} />;
}

/** Monospace uppercase micro-label (section eyebrows, table headers, metric labels). */
export function MicroLabel({ as: Tag = 'p', className, ...rest }: HTMLAttributes<HTMLElement> & { as?: 'p' | 'span' | 'div' }) {
  return <Tag {...rest} className={['qm-micro', className].filter(Boolean).join(' ')} />;
}

/**
 * Editorial page header: optional mono eyebrow, oversized tight Inter Tight title (with an optional serif pivot),
 * intro line and actions. `art` is kept for API compatibility; the circle geometry was retired (Nov 2026).
 */
export function PageHeader({ title, pivot, eyebrow, intro, actions, id = 'page-title', art = false, children }: {
  title: ReactNode; pivot?: boolean | ReactNode; eyebrow?: ReactNode; intro?: ReactNode; actions?: ReactNode; id?: string; art?: boolean; children?: ReactNode;
}) {
  return (
    <header className="qm-pagehead" data-art={art ? 'true' : undefined}>
      <div className="qm-pagehead__text">
        {eyebrow ? <MicroLabel className="qm-pagehead__eyebrow">{eyebrow}</MicroLabel> : null}
        <Heading level={1} id={id} pivot={pivot}>{title}</Heading>
        {intro ? <Text tone="muted" className="qm-pagehead__intro">{intro}</Text> : null}
        {children}
      </div>
      {actions ? <div className="qm-pagehead__actions">{actions}</div> : null}
    </header>
  );
}
